import { z } from '@hono/zod-openapi'

// id
export const idZ = z.number().openapi({ description: 'ファイルID', example: 1 })
export type id = z.infer<typeof idZ>

// ownerMemoId
export const ownerMemoIdZ = z.number().nullable().openapi({
  description: '紐づくオーナーメモID。null は当該用途のファイルではない',
  example: 101,
})
export type ownerMemoId = z.infer<typeof ownerMemoIdZ>

// ticketMessageId
export const ticketMessageIdZ = z.number().nullable().openapi({
  description: '紐づくチケットメッセージID。null は当該用途のファイルではない',
  example: 201,
})
export type ticketMessageId = z.infer<typeof ticketMessageIdZ>

// tenantId
export const tenantIdZ = z.number().nullable().openapi({
  description: '紐づく賃借人ID。null は当該用途のファイルではない',
  example: 301,
})
export type tenantId = z.infer<typeof tenantIdZ>

// tenantApplicationId
export const tenantApplicationIdZ = z.number().nullable().openapi({
  description: '紐づく入居申込ID。null は当該用途のファイルではない',
  example: 401,
})
export type tenantApplicationId = z.infer<typeof tenantApplicationIdZ>

// tenantFileType (tenant用ファイル種別)
export const TENANT_FILE_TYPE_RENTAL_CONTRACT = 'rental_contract' // 賃貸借契約書
export const TENANT_FILE_TYPE_IMPORTANT_MATTERS = 'important_matters' // 重要事項説明書
export const TENANT_FILE_TYPE_OTHER_IMAGE = 'other_image' // その他画像
export const tenantFileTypeZ = z
  .enum([
    TENANT_FILE_TYPE_RENTAL_CONTRACT,
    TENANT_FILE_TYPE_IMPORTANT_MATTERS,
    TENANT_FILE_TYPE_OTHER_IMAGE,
  ])
  .nullable()
  .openapi({
    description:
      '賃借人まわりのファイル種別（rental_contract:賃貸借契約書 / important_matters:重要事項説明書 / other_image:その他画像）',
    example: TENANT_FILE_TYPE_RENTAL_CONTRACT,
  })
export type tenantFileType = z.infer<typeof tenantFileTypeZ>

// tenantFileType に対応するタイトル
export const TENANT_FILE_TITLE_RENTAL_CONTRACT = '賃貸借契約書'
export const TENANT_FILE_TITLE_IMPORTANT_MATTERS = '重要事項説明書'

// tenantFileType からタイトルを取得するためのマッピング
export const TENANT_FILE_TYPE_TITLES: Record<
  | typeof TENANT_FILE_TYPE_RENTAL_CONTRACT
  | typeof TENANT_FILE_TYPE_IMPORTANT_MATTERS
  | typeof TENANT_FILE_TYPE_OTHER_IMAGE,
  string
> = {
  [TENANT_FILE_TYPE_RENTAL_CONTRACT]: TENANT_FILE_TITLE_RENTAL_CONTRACT,
  [TENANT_FILE_TYPE_IMPORTANT_MATTERS]: TENANT_FILE_TITLE_IMPORTANT_MATTERS,
  [TENANT_FILE_TYPE_OTHER_IMAGE]: 'その他画像',
}

// path (S3オブジェクトパス)
export const pathZ = z.string().min(1).openapi({
  description: 'ストレージ上の保存パス（S3オブジェクトキー）',
  example: 'owner_memos/101/1730620800000-uuid',
})
export type path = z.infer<typeof pathZ>

// fileName (オリジナルファイル名)
export const fileNameZ = z.string().nullable().openapi({
  description: 'アップロード時の元ファイル名',
  example: 'document.pdf',
})
export type fileName = z.infer<typeof fileNameZ>

// title (ファイルのタイトル)
export const titleZ = z.string().nullable().openapi({
  description: '画面表示用のファイルタイトル',
  example: 'マイドキュメント',
})
export type title = z.infer<typeof titleZ>

// fileExt (ファイル拡張子)
export const fileExtZ = z
  .string()
  .nullable()
  .openapi({ description: 'ファイル拡張子', example: '.pdf' })
export type fileExt = z.infer<typeof fileExtZ>

// fileType (MIMEタイプ)
export const fileTypeZ = z
  .string()
  .nullable()
  .openapi({ description: 'MIMEタイプ', example: 'application/pdf' })
export type fileType = z.infer<typeof fileTypeZ>

// uploadContentType (アップロードを受け付ける MIME タイプ)
// HTML や SVG など、ブラウザで開くとスクリプトが動く種類は配信ドメインでの XSS になるため除外する
export const UPLOAD_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/heic',
  'image/heif',
] as const
export const uploadContentTypeZ = z
  .enum(UPLOAD_CONTENT_TYPES)
  .openapi({ description: 'MIMEタイプ', example: 'application/pdf' })
export type uploadContentType = z.infer<typeof uploadContentTypeZ>

// fileSize (バイト数)
export const fileSizeZ = z
  .number()
  .nullable()
  .openapi({ description: 'ファイルサイズ（バイト）', example: 1024000 })
export type fileSize = z.infer<typeof fileSizeZ>

// echoesPath (Echoes 同期元の契約書PDFパス)
export const echoesPathZ = z.string().nullable().openapi({
  description:
    'Echoes 同期でコピーした契約書PDFの元パス。null は AgentCall でアップロードされたファイル',
  example: null,
})
export type echoesPath = z.infer<typeof echoesPathZ>

// echoesUpdatedAt (Echoes 側 contract_pdf の updated_at)
export const echoesUpdatedAtDateZ = z
  .date()
  .nullable()
  .openapi({ example: null })
export type echoesUpdatedAtDate = z.infer<typeof echoesUpdatedAtDateZ>

// createdAt
export const createdAtStringZ = z.string().openapi({
  description: '作成日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type createdAtString = z.infer<typeof createdAtStringZ>

export const createdAtDateZ = z
  .date()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type createdAtDate = z.infer<typeof createdAtDateZ>

// updatedAt
export const updatedAtStringZ = z.string().openapi({
  description: '更新日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type updatedAtString = z.infer<typeof updatedAtStringZ>

export const updatedAtDateZ = z
  .date()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type updatedAtDate = z.infer<typeof updatedAtDateZ>

// url
export const urlZ = z.string().openapi({
  description: 'ファイル取得用の署名付きURL（一定時間で失効する）',
  example: 'https://example.s3.ap-northeast-1.amazonaws.com/...',
})
export type url = z.infer<typeof urlZ>
